import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useIntl } from 'react-intl';
import DOMPurify from 'dompurify';
import { Divider, Typography, Box, Collapse, Button, IconButton, Skeleton, Chip, Tooltip } from '@mui/material';
import {
  Help as HelpIcon,
  TimeToLeave,
  NearMe,
  AspectRatio,
  Call,
  Directions,
  Group as GroupIcon,
  ArrowBackIos,
  ArrowForwardIos,
  LocationOn as LocationOnIcon,
  Person as PersonIcon,
  Lock as LockIcon,
  ArrowRightAlt as ArrowRightAltIcon,
  Notes as NotesIcon,
  Event as CalendarIcon,
  Info as InfoIcon,
  Cancel,
} from '@mui/icons-material';
import moment from 'moment';
import Linkify from 'react-linkify';
import { Timeline, TimelineConnector, TimelineContent, TimelineDot, TimelineItem, TimelineSeparator } from '@mui/lab';
import { Colors } from '../../assets/theme/Colors';
import { formatTime, formatRescueDistance, formatRescueDuration, formatPhoneNumber } from '../../helpers/formatters';
import grungeRed from '../../assets/images/grunge-red.png';
import grungeGreen from '../../assets/images/grunge-green.png';
import {
  getRescueDropoffAddress,
  getRescuePickupAddress,
  getRescuePickupLocationFullName,
  getRescuePosition,
  isHTML,
  showAddToCalendarButton,
} from '../../helpers/RescuesHelper';
import useActiveUser from '../../hooks/useActiveUser';
import { getUserSitesByRoles } from '../../services/auth';
import { Roles } from '../../models/roles';
import DonorLinkPickupIndicator from '../../components/DonorLinkPickupIndicator';
import useHasActiveFeatureFlag from '../../hooks/useFeatureFlags';
import { FF_DONOR_LINK, FF_GOOGLE_CALENDAR } from '../../components/FeatureFlagWrapper';
import { isInPWAView } from '../../components/Add2HS/PWAInstallButton';
import routes from '../../routes';
import { addRescueToCalendar, removeRescueFromCalendar, getCalendarAuthUrl, getCalendarStatus } from '../../api/googleCalendar';
import snackbarHelper from '../../helpers/snackbarHelper';

export const RescueColor = ({ children, visibility, color }) => (
  <Box
    sx={{
      position: 'absolute',
      height: '100%',
      width: '5px',
      backgroundColor: color || Colors.appBar.light,
      borderTopLeftRadius: '16px',
      borderBottomLeftRadius: '16px',
    }}
    visibility={visibility}
  >
    {children}
  </Box>
);

export const DialButton = ({ phone, text = 'Call', ...rest }) => (
  <Button
    {...rest}
    variant="outlined"
    startIcon={<Call />}
    sx={{
      width: 55,
      height: 55,

      '& .MuiButton-startIcon': {
        m: 0,
        mt: '5px',
        mb: '2px',
      },

      '& .MuiButton-label': {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      },
    }}
    onClick={() => {
      window.location.href = `tel:${phone}`;
    }}
  >
    <Box>{text}</Box>
  </Button>
);

export const MapButton = ({ address, text = 'Map', handleAddressClick, ...rest }) => (
  <IconButton
    {...rest}
    sx={{
      maxWidth: 20,
      minWidth: 20,
      height: 20,
      marginLeft: '8px',

      display: 'flex',
      alignItems: 'center',
    }}
    color="primary"
    onClick={() => handleAddressClick(address)}
    size="large"
  >
    <Directions sx={{ margin: 0, marginTop: '5px', marginBottom: '2px' }} />
    <Box sx={{ fontSize: '10px', display: 'flex', alignItems: 'center' }}>{text}</Box>
  </IconButton>
);

export const RubberStamp = ({ text, visibility, isCancelled, isClosed, isClaimed, isUnclaimed, ...rest }) => {
  const styles = {
    cancelled: {
      borderImage: `url(${grungeRed}) 30`,
      color: Colors.rescues.cancelled.color,
      borderColor: Colors.rescues.cancelled.color,
    },
    closed: {
      borderImage: `url(${grungeGreen}) 30`,
      color: Colors.rescues.closed.color,
      borderColor: Colors.rescues.closed.color,
    },
    claimed: {
      borderImage: `url(${grungeGreen}) 30`,
      color: Colors.rescues.claimed.color,
      borderColor: Colors.rescues.claimed.color,
      fontWeight: 'bold',
      top: '35%',
    },
    unclaimed: {
      color: Colors.rescues.unclaimed.color,
      borderColor: Colors.rescues.unclaimed.color,
      fontWeight: 'bold',
      top: '35%',
    },
  };

  return (
    <Box
      {...rest}
      sx={[
        {
          textAlign: 'center',
          position: 'absolute',
          display: 'inline-block',
          borderRadius: 0,
          borderWidth: '8px',
          borderStyle: 'solid',
          textTransform: 'uppercase',
          margin: '0 auto',
          letterSpacing: -3,
          transformOrigin: '50% 50%',
          msTransform: 'rotate(30deg)',
          WebkitTransform: 'rotate(30deg)',
          transform: 'rotate(30deg)',
          padding: '5px',
          border: '0.5rem solid #F44336',
          WebkitMaskPosition: '13rem 6rem',
          opacity: 0.6,
          top: 90,
          left: 'calc(50% - 90px)',
        },
        isCancelled && styles.cancelled,
        isClosed && styles.closed,
        isClaimed && styles.claimed,
        isUnclaimed && styles.unclaimed,
      ]}
      visibility={visibility}
    >
      <Box fontSize="body1.fontSize" letterSpacing={1.5}>
        {text}
      </Box>
    </Box>
  );
};

export const AddressLine = ({ children, ...rest }) => (
  <Box
    sx={{
      borderColor: Colors.appBar.light,
      background: `repeating-linear-gradient(${Colors.appBar.light}, ${Colors.appBar.light} 3px, transparent 3px, transparent 6px)`,
      width: 2,
      marginTop: '12px',
      marginBottom: '12px',
      marginRight: '5px',
      marginLeft: '2px',
      position: 'relative',
      '&:before, &::after': {
        content: '""',
        position: 'absolute',
        left: -3,
        width: 8,
        height: 8,
        borderRadius: '50%',
        backgroundColor: '#fff',
        border: '2px solid',
        borderColor: 'inherit',
      },
      '&:before': {
        top: 0,
      },
      '&:after': {
        bottom: 0,
      },
    }}
    {...rest}
  >
    {children}
  </Box>
);

const breakWhiteSpacesSx = {
  whiteSpace: 'pre-wrap',
  '& p': {
    typography: 'body2',
    margin: '0 !important',
  },
};
const collapsableSx = {
  transition: 'all 0.3s ease-in-out',
  overflow: 'hidden',
};
const hideWhenExpandedSx = {
  height: 0,
  opacity: 0,
  margin: 0,
  padding: 0,
  transition: 'all 0.3s ease-in-out, opacity 1ms',
};
const timelineSx = {
  margin: 0,
  padding: 0,
};
const timelineItemSx = {
  minHeight: '20px',
  '&:before': {
    display: 'none',
  },
};
const timelineLineConnectorSx = {
  borderColor: Colors.appBar.light,
  background: `repeating-linear-gradient(${Colors.appBar.light}, ${Colors.appBar.light} 3px, transparent 3px, transparent 6px)`,
};
const timeLineDotSx = {
  marginTop: 1,
  padding: '2px',
  borderRadius: '50%',
  backgroundColor: '#fff',
  border: '2px solid',
  color: Colors.appBar.light,
};
const timeLineContentSx = {
  paddingTop: 0,
  paddingRight: 0,
  paddingLeft: '8px',
  marginBottom: '8px',
};
const timeLineContentLastSx = {
  paddingTop: 0,
  paddingRight: 0,
  paddingLeft: '8px',
  marginBottom: 0,
  paddingBottom: 0,
};
const timeLineIconSx = {
  color: Colors.appBar.light,
  marginRight: '8px',
};
const linkSx = {
  textDecoration: 'none',
  color: 'primary.main',
  display: 'flex',
  alignItems: 'center',
};

export default function RescueMobileCard({
  rescue,
  children,
  isLoading,
  highlighted,
  isCancelled = false,
  isClosed = false,
  hideExpandPanel = false,
  isClaimed = false,
  isUnclaimed = false,
  noShadow = false,
  hideLocations = false,
  currentSlide = 0,
  maxSlides = 0,
  isRescueSlider = false,
  hideDonorLink = false,
  isReceiverView = false,
  isDonorView = false,
  showCancel = false,
  isRecent = false,
  claimer = {},
  isPinned = false,
  handleCancelClick = () => {},
  handleSliderNext = () => {},
  handleSliderPrev = () => {},
  rescueSliderRef = {},
}) {
  const activeUser = useActiveUser();
  const [expanded, setExpanded] = useState(false);
  const [isAddedToCalendar, setIsAddedToCalendar] = useState(rescue?.in_google_calendar || false);
  const { formatMessage } = useIntl();
  const hasActiveDonorLinkFF = useHasActiveFeatureFlag(FF_DONOR_LINK);
  const hasActiveGoogleCalendarFF = useHasActiveFeatureFlag(FF_GOOGLE_CALENDAR);

  useEffect(() => {
    setIsAddedToCalendar(rescue?.in_google_calendar || false);
  }, [rescue?.in_google_calendar]);

  useEffect(() => {
    if (isRescueSlider) {
      setExpanded(false);
    }
  }, [currentSlide, isRescueSlider]);

  const handleAddressClick = (address) =>
    isInPWAView
      ? window.open(`https://maps.google.com/?q=${address}`, '_top')
      : window.open(`https://maps.google.com/?q=${address}`, '_blank');

  const handleCalendarClick = async () => {
    try {
      if (isAddedToCalendar) {
        await removeRescueFromCalendar(activeUser.id, rescue.id);

        setIsAddedToCalendar(false);

        snackbarHelper.success('Rescue removed from Google Calendar');
      } else {
        const statusResponse = await getCalendarStatus(activeUser.id);
        const statusData = await statusResponse.json();

        if (!statusData.data.connected) {
          const redirectUrl = new URL(window.location.href);

          redirectUrl.searchParams.set('gcal_add_rescue', rescue.id);

          const authUrlResponse = await getCalendarAuthUrl(activeUser.id, redirectUrl.toString());
          const authUrlData = await authUrlResponse.json();

          window.location.href = authUrlData.data.auth_url;
        } else {
          await addRescueToCalendar(activeUser.id, rescue.id);

          setIsAddedToCalendar(true);

          snackbarHelper.success('Rescue added to Google Calendar');
        }
      }
    } catch (error) {
      snackbarHelper.error(error.message || 'Failed to update calendar');
    }
  };

  // auto-add rescue to calendar after returning from OAuth flow
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const gcalConnected = params.get('gcal_connected');
    const gcalAddRescue = params.get('gcal_add_rescue');

    if (gcalConnected === 'true' && gcalAddRescue && rescue && gcalAddRescue === String(rescue.id)) {
      params.delete('gcal_connected');
      params.delete('gcal_add_rescue');

      const cleanUrl = `${window.location.pathname}${params.toString() ? `?${params}` : ''}${window.location.hash}`;

      window.history.replaceState({}, '', cleanUrl);

      addRescueToCalendar(activeUser.id, rescue.id)
        .then(() => {
          setIsAddedToCalendar(true);

          snackbarHelper.success('Rescue added to Google Calendar');
        })
        .catch(err => snackbarHelper.error(err.message || 'Failed to add rescue to calendar'));
    }
  }, [rescue?.id]);

  const dayName = rescue
    ? moment(rescue.date).calendar(null, {
        sameDay: `[${formatMessage({ id: 'dates.today' })}]`,
        nextDay: `[${formatMessage({ id: 'dates.tomorrow' })}]`,
        nextWeek: 'dddd, MMM D',
        lastDay: `[${formatMessage({ id: 'dates.yesterday' })}]`,
        lastWeek: `[${formatMessage({ id: 'dates.last' })}] dddd, MMM D`,
        sameElse: date => (moment().isSame(date, 'year') ? 'dddd, MMM D' : 'dddd, MMM D YYYY'),
      })
    : null;
  const rescueTime = rescue
    ? `${formatTime(rescue.pickup_begin)} - ${formatTime(rescue.pickup_end)}`
    : null;
  const rescueName = rescue ? getRescuePickupLocationFullName(rescue) : null;
  const handleExpandClick = () => setExpanded(!expanded);
  const rescuePickupAddress = rescue ? getRescuePickupAddress(rescue) : null;
  const rescueDropoffAddress = rescue ? getRescueDropoffAddress(rescue) : null;
  const sitesWithRescuerRoles = getUserSitesByRoles(activeUser.role_assignments, Roles.Rescuer);

  return (
    <Box
      boxShadow={noShadow ? 0 : 25}
      sx={[
        {
          position: 'relative',
          backgroundColor: '#fff',
          width: '100%',
          borderRadius: '16px',
          borderTopLeftRadius: highlighted ? '8px' : '16px',
          borderBottomLeftRadius: highlighted ? '8px' : '16px',
        },
        isPinned &&
          !isClaimed &&
          !isCancelled &&
          !isClosed &&
          !expanded && { backgroundColor: Colors.donationGroups.light },
      ]}
      data-introid={isRecent ? 'recent-rescue' : 'upcoming-rescue'}
    >
      <RubberStamp
        isCancelled
        visibility={isCancelled ? 'visible' : 'hidden'}
        text={formatMessage({ id: 'rescuer-dashboard-mobile.rescue-card.stamps.cancelled' })}
      />
      <RubberStamp
        isClaimed
        visibility={isClaimed ? 'visible' : 'hidden'}
        text={formatMessage({ id: 'rescuer-dashboard-mobile.rescue-card.stamps.claimed' })}
      />
      <RubberStamp
        isClosed
        visibility={!isLoading && isClosed ? 'visible' : 'hidden'}
        text={
          <Box display="flex" justifyContent="center">
            <LockIcon /> {formatMessage({ id: 'rescuer-dashboard-mobile.rescue-card.stamps.closed' })}
          </Box>
        }
      />
      <RubberStamp
        isUnclaimed
        visibility={!isLoading && isUnclaimed && !isCancelled ? 'visible' : 'hidden'}
        text={formatMessage({ id: 'rescuer-dashboard-mobile.rescue-card.stamps.unclaimed' })}
      />

      <RescueColor visibility={highlighted ? 'visible' : 'hidden'} />

      <Box display="flex" py={2} sx={[isClaimed && { opacity: 0.3 }]}>
        <Box
          flexGrow={1}
          position="relative"
          display="flex"
          justifyContent="flex-start"
          flexDirection="column"
          alignItems="stretch"
        >
          <Box pl={2} display="flex" flexDirection="column" pr={2}>
            <Box display="flex" flexDirection="column">
              <Typography variant="subtitle1">{isLoading ? <Skeleton /> : dayName}</Typography>

              <Typography variant="subtitle2">{isLoading ? <Skeleton /> : 'Pickup Window:'}</Typography>

              <Typography variant="subtitle2">
                {isLoading ? <Skeleton /> : rescueTime}
              </Typography>

              {!isLoading && hasActiveGoogleCalendarFF && showAddToCalendarButton(rescue, activeUser) && (
                <Typography variant="subtitle2">
                  <Box component="a" sx={linkSx} onClick={() => handleCalendarClick()}>
                    <CalendarIcon fontSize="small" />

                    {isAddedToCalendar ? 'Remove from Google Calendar' : 'Add to Google Calendar'}

                    {!isAddedToCalendar && (
                      <Chip
                        label="NEW"
                        size="small"
                        color="primary"
                        style={{ height: 16, fontSize: 10, fontWeight: 700, marginLeft: 4, verticalAlign: 'middle' }}
                      />
                    )}
                  </Box>
                </Typography>
              )}

              {!isLoading && sitesWithRescuerRoles.length > 1 && (
                <Typography variant="body2">Site: {rescue.site}</Typography>
              )}
            </Box>

            <Box display="flex" py={1} sx={[collapsableSx, expanded && hideWhenExpandedSx]}>
              {isReceiverView || isDonorView ? (
                // Receiver view
                <Timeline sx={timelineSx}>
                  {/* Food Donor */}
                  <TimelineItem sx={timelineItemSx}>
                    <TimelineSeparator>
                      <TimelineDot sx={timeLineDotSx} />
                      <TimelineConnector sx={timelineLineConnectorSx} />
                    </TimelineSeparator>
                    <TimelineContent sx={timeLineContentSx}>
                      <Box>
                        <Typography variant="body1">{isLoading ? <Skeleton /> : rescueName}</Typography>
                        <Typography variant="body2">
                          {isLoading ? <Skeleton /> : `${rescue.pickup_city}, ${rescue.pickup_st}`}
                        </Typography>
                      </Box>
                      <Box mt={2}>
                        <Divider />
                      </Box>
                    </TimelineContent>
                  </TimelineItem>
                  {/* Claimer */}
                  <TimelineItem sx={timelineItemSx}>
                    <TimelineSeparator>
                      <TimelineDot sx={timeLineDotSx} />
                      <TimelineConnector sx={timelineLineConnectorSx} />
                    </TimelineSeparator>
                    <TimelineContent sx={timeLineContentSx}>
                      {claimer?.name ? (
                        <Typography variant="body1" style={{ fontWeight: 'bold' }}>
                          {isLoading ? <Skeleton /> : `Rescue by ${claimer.name}`}
                        </Typography>
                      ) : (
                        <Typography variant="body1">
                          {isLoading ? <Skeleton /> : 'Rescue has not been claimed yet'}
                        </Typography>
                      )}
                      <Box mt={2}>
                        <Divider />
                      </Box>
                    </TimelineContent>
                  </TimelineItem>
                  {/* Receiver */}
                  <TimelineItem sx={timelineItemSx}>
                    <TimelineSeparator>
                      <TimelineConnector sx={timelineLineConnectorSx} />
                      <TimelineDot sx={timeLineDotSx} />
                    </TimelineSeparator>
                    <TimelineContent sx={timeLineContentLastSx}>
                      <Box>
                        <Typography variant="body1">{isLoading ? <Skeleton /> : rescue.receiver}</Typography>

                        <Typography variant="body2">
                          {isLoading ? <Skeleton /> : `${rescue.dropoff_city}, ${rescue.dropoff_st}`}
                        </Typography>
                      </Box>
                    </TimelineContent>
                  </TimelineItem>
                </Timeline>
              ) : (
                // Rescuer
                <>
                  <AddressLine />
                  <Box display="flex" flexDirection="column" flexGrow={1} pl={1}>
                    <Box my={0.5}>
                      <Typography variant="body1">{isLoading || hideLocations ? <Skeleton /> : rescueName}</Typography>

                      <Typography variant="body2">
                        {isLoading || hideLocations ? <Skeleton /> : `${rescue.pickup_city}, ${rescue.pickup_st}`}
                      </Typography>
                    </Box>

                    <Box my={0.5}>
                      <Divider />
                    </Box>

                    <Box my={0.5}>
                      <Typography variant="body1">
                        {isLoading || hideLocations ? <Skeleton /> : rescue.receiver}
                      </Typography>

                      <Typography variant="body2">
                        {isLoading || hideLocations ? <Skeleton /> : `${rescue.dropoff_city}, ${rescue.dropoff_st}`}
                      </Typography>
                    </Box>
                  </Box>
                </>
              )}
            </Box>
          </Box>

          {/* Rescue buttons */}
          {!hideExpandPanel && (
            <Box display="block" py={0} pl={2} pr={1} sx={[collapsableSx, expanded && hideWhenExpandedSx]}>
              {children}
            </Box>
          )}
        </Box>

        <Box
          px={0.5}
          color="text.secondary"
          borderLeft={1}
          borderColor="grey.500"
          display="flex"
          direction="column"
          alignItems="center"
          width={85}
          maxwidth={85}
          sx={{
            overflow: 'hidden',
            wordBreak: 'break-word',
          }}
        >
          <Box display="flex" flexGrow={1} flexDirection="column" alignItems="center">
            <Box
              flexGrow={1}
              flexBasis={0}
              flexDirection="column"
              color="text.secondary"
              sx={[expanded && hideWhenExpandedSx]}
            >
              {hasActiveDonorLinkFF && !isLoading && !!rescue?.location_donor_link_active && !hideDonorLink && (
                <Box pb={1} flexGrow={1} flexBasis={0} sx={[collapsableSx, expanded && hideWhenExpandedSx]}>
                  <DonorLinkPickupIndicator pickupReady={rescue?.rescue_confirmed} isMobile />
                </Box>
              )}

              {showCancel && !isCancelled && (
                <Box
                  flexDirection="column"
                  textAlign="center"
                  pb={1}
                  onClick={() => handleCancelClick(rescue)}
                  sx={[collapsableSx, expanded && hideWhenExpandedSx]}
                >
                  <Cancel color="error" />
                </Box>
              )}

              {rescue && rescue.positions > 1 && (
                <Box
                  pb={1}
                  flexGrow={1}
                  flexBasis={0}
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  textAlign="center"
                  sx={[collapsableSx, expanded && hideWhenExpandedSx]}
                >
                  <GroupIcon />
                  <Typography variant="caption">{isLoading ? <Skeleton /> : getRescuePosition(rescue)}</Typography>
                </Box>
              )}

              <Box
                flexDirection="column"
                textAlign="center"
                pb={1}
                onClick={() => handleAddressClick(rescuePickupAddress)}
                sx={[collapsableSx, expanded && hideWhenExpandedSx]}
              >
                <NearMe />

                <Typography variant="body2">
                  {isLoading ? <Skeleton /> : formatRescueDistance(rescue.distance)}
                </Typography>
              </Box>

              <Box
                flexDirection="column"
                textAlign="center"
                pb={1}
                sx={[collapsableSx, expanded && hideWhenExpandedSx]}
              >
                <TimeToLeave />

                <Typography variant="body2">
                  {isLoading ? <Skeleton /> : formatRescueDuration(rescue.duration)}
                </Typography>
              </Box>

              <Box
                flexGrow={1}
                flexBasis={0}
                display="flex"
                flexDirection="column"
                alignItems="center"
                textAlign="center"
                sx={[collapsableSx, expanded && hideWhenExpandedSx]}
              >
                <AspectRatio />
                <Typography variant="caption">{isLoading ? <Skeleton /> : rescue.rescue_size || 'n/a'}</Typography>
              </Box>
            </Box>
            {!hideExpandPanel && (
              <Box
                data-introid={isLoading ? '' : 'rescue-info'}
                width={1}
                onClick={handleExpandClick}
                sx={{
                  backgroundColor: isPinned && !expanded ? Colors.donationGroups.main : '#efefef',
                  borderRadius: '20%',
                  width: 55,
                  height: 55,
                  paddingTop: '10px',
                  marginTop: '16px',
                }}
                display="flex"
                flexDirection="column"
                alignItems="center"
                textAlign="center"
              >
                <HelpIcon htmlColor={Colors.blue.main} />
                <Typography variant="caption">
                  {expanded
                    ? formatMessage({ id: 'rescuer-dashboard-mobile.rescue-card.hide' })
                    : formatMessage({ id: 'rescuer-dashboard-mobile.rescue-card.info' })}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
      {!hideExpandPanel && (
        <Collapse
          in={expanded}
          // Use to ensure the slider calculates card size correctly and that the exit animation remains smooth
          onEnter={() => {
            rescueSliderRef?.current?.innerSlider?.onWindowResized();
          }}
          onExited={() => {
            rescueSliderRef?.current?.innerSlider?.onWindowResized();
          }}
          timeout="auto"
          unmountOnExit
        >
          <Box px={2}>
            <Divider />
          </Box>
          <Box
            p={2}
            flexDirection="column"
            color="text.secondary"
            // Prevents drag to enable text copy
            onMouseDown={(e) => e.stopPropagation()}
          >
            {isPinned && (
              <Box
                sx={{
                  mt: 1,
                  mb: 2,
                  p: 1,
                  display: 'flex',
                  alignItems: 'center',
                  backgroundColor: Colors.donationGroups.light,
                  borderRadius: 2,
                }}
              >
                <InfoIcon htmlColor={Colors.blue.main} />
                <Typography variant="body2" sx={{ ml: 1 }}>
                  This rescue is reserved for a small group of volunteers in your site, and you’re one of them! Please
                  claim if you can.
                </Typography>
              </Box>
            )}

            {!isLoading && rescue.rescuer_notes && (
              <>
                <Box>
                  <Typography variant="subtitle1">
                    {formatMessage({ id: 'rescuer-dashboard-mobile.rescue-card.notes' })}
                  </Typography>

                  <Typography variant="body1" sx={breakWhiteSpacesSx}>
                    {isHTML(rescue?.rescuer_notes) ? (
                      <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(rescue?.rescuer_notes) }} />
                    ) : (
                      <Linkify
                        componentDecorator={(decoratedHref, decoratedText, key) => (
                          <a target="blank" href={decoratedHref} key={key}>
                            {decoratedText}
                          </a>
                      )}
                      >
                        <Box component="span" sx={breakWhiteSpacesSx}>{rescue?.rescuer_notes}</Box>
                      </Linkify>
                    )}
                  </Typography>
                </Box>

                <Box my={2}>
                  <Divider />
                </Box>
              </>
            )}

            {!isLoading && rescue.slug && (
              <>
                <Box>
                  <Typography variant="subtitle1">
                    {formatMessage({ id: 'rescuer-dashboard-mobile.rescue-card.description' })}
                  </Typography>

                  <Typography variant="body1" sx={breakWhiteSpacesSx}>
                    {rescue.slug}
                  </Typography>
                </Box>

                <Box my={2}>
                  <Divider />
                </Box>
              </>
            )}

            <Timeline sx={timelineSx}>
              {/* Drive to FD */}
              <TimelineItem sx={timelineItemSx}>
                <TimelineSeparator>
                  <TimelineDot sx={timeLineDotSx}/>
                  <TimelineConnector sx={timelineLineConnectorSx} />
                </TimelineSeparator>
                <TimelineContent sx={timeLineContentSx}>
                  <Box display="flex" alignItems="center" gap="8px">
                    <Box>
                      <Typography variant="body1" style={{ fontWeight: 'bold' }}>
                        {isLoading || hideLocations ? <Skeleton /> : `Drive to ${rescue.location}`}
                      </Typography>
                    </Box>
                    <Box>
                      <MapButton
                        address={rescuePickupAddress}
                        text={formatMessage({ id: 'rescuer-dashboard-mobile.rescue-card.map-button' })}
                        handleAddressClick={handleAddressClick}
                      />
                    </Box>
                  </Box>
                </TimelineContent>
              </TimelineItem>

              {/* Pickup info */}
              <TimelineItem sx={timelineItemSx}>
                <TimelineSeparator>
                  <TimelineDot sx={timeLineDotSx} />
                  <TimelineConnector sx={timelineLineConnectorSx} />
                </TimelineSeparator>
                <TimelineContent sx={timeLineContentSx}>
                  <Box>
                    {!isLoading ? (
                      <Box
                        display="flex"
                        flexDirection="column"
                        justifyContent="space-between"
                        alignItems="flex-start"
                        gap="8px"
                      >
                        {/* Food Donor name */}
                        <Box>
                          <Typography variant="body1" style={{ fontWeight: 'bold' }}>
                            {rescue.location}
                          </Typography>
                        </Box>
                        {/* Pickup address   */}
                        <Box display="flex">
                          <LocationOnIcon fontSize="small" sx={timeLineIconSx} />
                          <Typography variant="body2">{rescuePickupAddress}</Typography>
                        </Box>
                        {/* Pickup contact */}
                        <Box display="flex">
                          <PersonIcon fontSize="small" sx={timeLineIconSx} />
                          <Box display="flex" flexWrap="wrap">
                            <Typography variant="body2" display="inline">
                              {formatMessage({ id: 'rescuer-dashboard-mobile.rescue-card.pickup-contact' })}{' '}
                              {rescue.pickup_contact ? `${rescue.pickup_contact}, ` : ''}
                              {rescue.pickup_contact_phone ? (
                                <a href={`tel:${rescue.pickup_contact_phone}`}>
                                  {`(${formatPhoneNumber(rescue.pickup_contact_phone)}), `}
                                </a>
                              ) : (
                                ''
                              )}
                              {rescue.pickup_contact_email ? (
                                <a href={`mailto:${rescue.pickup_contact_email}`}>{rescue.pickup_contact_email}</a>
                              ) : (
                                ''
                              )}
                            </Typography>
                          </Box>
                        </Box>
                        {/* Pickup notes */}
                        {rescue.pickup_notes && (
                          <Box>
                            <Box display="flex" alignItems="center">
                              <NotesIcon fontSize="small" sx={timeLineIconSx} />
                              <Typography variant="body2">
                                {formatMessage({ id: 'rescuer-dashboard-mobile.rescue-card.pickup-notes' })}
                              </Typography>
                            </Box>
                            <Box ml="28px">
                              <Typography variant="body2" sx={breakWhiteSpacesSx}>
                                {isHTML(rescue?.pickup_notes) ? (
                                  <span
                                    dangerouslySetInnerHTML={{
                                      __html: DOMPurify.sanitize(rescue?.pickup_notes),
                                    }}
                                  />
                                ) : (
                                  <Linkify
                                    componentDecorator={(decoratedHref, decoratedText, key) => (
                                      <a target="blank" href={decoratedHref} key={key}>
                                        {decoratedText}
                                      </a>
                                  )}
                                  >
                                    <Box component="span" sx={breakWhiteSpacesSx}>{rescue?.pickup_notes}</Box>
                                  </Linkify>
                                )}
                              </Typography>
                            </Box>
                          </Box>
                        )}
                        {/* Id badge */}
                        <Box component={Link} sx={linkSx} to={{ pathname: routes.idMode, state: { rescueId: rescue.id } }}>
                          Access your Food Rescue US ID Badge <ArrowRightAltIcon fontSize="small" />
                        </Box>
                      </Box>
                    ) : (
                      <Box pt={0.5}>
                        <Skeleton width="65%" />
                        <Skeleton width="85%" />
                        <Skeleton width="45%" />
                      </Box>
                    )}
                  </Box>
                </TimelineContent>
              </TimelineItem>

              {/* Drive to RA */}
              <TimelineItem sx={timelineItemSx}>
                <TimelineSeparator>
                  <TimelineDot sx={timeLineDotSx} />
                  <TimelineConnector sx={timelineLineConnectorSx} />
                </TimelineSeparator>
                <TimelineContent sx={timeLineContentSx}>
                  <Box display="flex" gap="8px">
                    <Box>
                      <Typography variant="body1" style={{ fontWeight: 'bold' }}>
                        {isLoading || hideLocations ? <Skeleton /> : `Drive to ${rescue.receiver}`}
                      </Typography>
                      <Box>
                        <Typography variant="body2" display="inline">
                          {rescue?.duration ? `${formatRescueDuration(rescue.duration)}, ` : ''}
                        </Typography>
                        <Typography variant="body2" display="inline">
                          {rescue?.distance ? formatRescueDistance(rescue.distance) : ''}
                        </Typography>
                      </Box>
                    </Box>
                    <Box>
                      <MapButton
                        address={rescueDropoffAddress}
                        text={formatMessage({ id: 'rescuer-dashboard-mobile.rescue-card.map-button' })}
                        handleAddressClick={handleAddressClick}
                      />
                    </Box>
                  </Box>
                </TimelineContent>
              </TimelineItem>

              {/* Dropoff info */}
              <TimelineItem sx={timelineItemSx}>
                <TimelineSeparator>
                  <TimelineDot sx={timeLineDotSx} />
                </TimelineSeparator>
                <TimelineContent sx={timeLineContentSx}>
                  <Box>
                    {!isLoading ? (
                      <Box
                        display="flex"
                        flexDirection="column"
                        justifyContent="space-between"
                        alignItems="flex-start"
                        gap="8px"
                      >
                        {/* RA name */}
                        <Box>
                          <Typography variant="body1" style={{ fontWeight: 'bold' }}>
                            {rescue.receiver}
                          </Typography>
                        </Box>
                        {/* Dropoff address   */}
                        <Box display="flex">
                          <LocationOnIcon fontSize="small" sx={timeLineIconSx} />
                          <Typography variant="body2">{rescueDropoffAddress}</Typography>
                        </Box>
                        {/* Dropoff contact */}
                        <Box display="flex">
                          <PersonIcon fontSize="small" sx={timeLineIconSx} />
                          <Box display="flex" flexWrap="wrap">
                            <Typography variant="body2" display="inline">
                              {formatMessage({ id: 'rescuer-dashboard-mobile.rescue-card.dropoff-contact' })}{' '}
                              {rescue.dropoff_contact ? `${rescue.dropoff_contact}, ` : ''}
                              {rescue.dropoff_contact_phone ? (
                                <a href={`tel:${rescue.dropoff_contact_phone}`}>
                                  {`(${formatPhoneNumber(rescue.dropoff_contact_phone)}), `}
                                </a>
                              ) : (
                                ''
                              )}
                              {rescue.dropoff_contact_email ? (
                                <a href={`mailto:${rescue.dropoff_contact_email}`}>{rescue.dropoff_contact_email}</a>
                              ) : (
                                ''
                              )}
                            </Typography>
                          </Box>
                        </Box>
                        {/* Dropoff notes */}
                        {rescue.dropoff_notes && (
                          <Box>
                            <Box display="flex" alignContent="center">
                              <NotesIcon fontSize="small" sx={timeLineIconSx} />
                              <Typography variant="body2">
                                {formatMessage({ id: 'rescuer-dashboard-mobile.rescue-card.dropoff-notes' })}
                              </Typography>
                            </Box>
                            <Box ml="28px">
                              <Typography variant="body2" sx={breakWhiteSpacesSx}>
                                {isHTML(rescue?.dropoff_notes) ? (
                                  <span
                                    dangerouslySetInnerHTML={{
                                      __html: DOMPurify.sanitize(rescue?.dropoff_notes),
                                    }}
                                  />
                                ) : (
                                  <Linkify
                                    componentDecorator={(decoratedHref, decoratedText, key) => (
                                      <a target="blank" href={decoratedHref} key={key}>
                                        {decoratedText}
                                      </a>
                                  )}
                                  >
                                    <Box component="span" sx={breakWhiteSpacesSx}>{rescue?.dropoff_notes}</Box>
                                  </Linkify>
                                )}
                              </Typography>
                            </Box>
                          </Box>
                        )}
                        {/* Id badge */}
                        <Box
                          component={Link}
                          sx={linkSx}
                          to={{ pathname: routes.idMode, state: { rescueId: rescue.id } }}
                        >
                          Access your Food Rescue US ID Badge <ArrowRightAltIcon fontSize="small" />
                        </Box>
                      </Box>
                    ) : (
                      <Box pt={0.5}>
                        <Skeleton width="65%" />
                        <Skeleton width="85%" />
                        <Skeleton width="45%" />
                      </Box>
                    )}
                  </Box>
                </TimelineContent>
              </TimelineItem>
            </Timeline>

            {/* Rescue buttons */}
            <Box display="block" py={0} pl={2} pr={1}>
              {children}
            </Box>
          </Box>
        </Collapse>
      )}
      {isRescueSlider && maxSlides > 1 && (
        <>
          <Box px={2}>
            <Divider />
          </Box>
          <Box display="flex" justifyContent="flex-end" alignItems="center">
            <IconButton onClick={handleSliderPrev} size="large">
              <ArrowBackIos />
            </IconButton>
            <Typography variant="subtitle2">
              {currentSlide + 1} / {maxSlides}
            </Typography>
            <IconButton onClick={handleSliderNext} size="large">
              <ArrowForwardIos />
            </IconButton>
          </Box>
        </>
      )}
    </Box>
  );
}
